/* configurator-core.js — the shape-adaptive configurator engine, shared by the agent order desk
 * (agent/desk.js) and the storefront PDP widget (theme/assets/screen-configurator.js).
 *
 * Extracted from what was agent/desk.js verbatim (Sep 2026) — this file changes NOTHING about how
 * any of it behaves, it only gives the logic a name and a way to be called from two different
 * shells instead of being copied a third, fourth, fifth time. desk.js's own header comment had
 * been flagging this debt since the second product: "extract a shared renderer rather than
 * copying this a third time."
 *
 * Deliberately NOT in here (shell-specific, stays in desk.js / the PDP shell):
 *   - how a request is authorised (agent: App Bridge session token; PDP: same-origin/App Proxy)
 *   - anything agent-only: Route/Mulberry protection, the "Taken by" agent picker, append-to-
 *     existing-draft, the save/email/invoice flow
 *   - anything PDP-only: the launcher/band open-on-demand UI, stock-size Add to Cart
 *   - page wiring (which elements exist, when to call render(), the gate/passcode check)
 *
 * A shell creates one of these per page:
 *
 *   var core = ConfiguratorCore(CFG, el, apiFn, readJson);
 *
 * CFG    — the same shape as the existing #desk-config blocks (product_key, min/max_*, labels,
 *          grommets, axes, components, variants, rate/setup/ladder fields, addons, derived).
 *          Mutated in place by core.applyVariant() on a variant-switch page, exactly as before.
 * el     — a plain object of DOM references. Required keys: h, hr, w, wr, hFt, wFt, d, dr, dFt,
 *          stage, price, priceSub, rows, specErr, stock. Optional: any of FIELDS (customer form).
 *          The shell builds this from whatever data-attribute convention its own markup uses —
 *          the core only ever reads through `el`, never queries the DOM directly by attribute.
 * apiFn  — function(path, options) -> Promise<Response>, with auth already attached. Same
 *          signature as desk.js's own api().
 * readJson — function(Response) -> Promise<{ok, status, data}>. Same as desk.js's own readJson().
 */
// attr — the DOM attribute the shell marks its interactive elements with, e.g. "data-d" for the
// agent desk, "data-sc" for the PDP widget. Only affects the elements the core queries directly
// by attribute (finish/addon/component/variant/mulberry, the derived-addon row); the elements
// passed in via `el` are unaffected, since the core only ever reads those through `el`. Defaults
// to "data-d" so agent/desk.js's existing call site (which passes no 5th argument) is unchanged.
window.ConfiguratorCore = function (CFG, el, apiFn, readJson, attr) {
  'use strict';

  var ATTR = attr || 'data-d';
  function q(name) { return '[' + ATTR + '="' + name + '"]'; }

  var VINYL_IN = 2, SPACING_IN = 12, CORNER_IN = 6, GROM_R = 1.6, GROM_HOLE = 0.62, PAD = 22;
  var OBL = 0.42;

  // Products differ in what their axes are called and whether the piece has grommets: a screen is
  // Height x Width with a bound, grommeted edge; turf is Width x Length, cut from a roll with
  // nothing to hang it by.
  var LBL = CFG.labels || { height: 'Height', width: 'Width' };
  var HAS_GROMMETS = CFG.grommets !== false;
  // Some items have one specified dimension only: a baffle is cut to width, a foam pad to length.
  var SINGLE = CFG.axes === 1;
  // An enclosure or a pipe frame has a third dimension, priced on the surface it wraps rather than
  // the face area. Some are sold as parts — a surround, a pipe frame, or both.
  var HAS_DEPTH = CFG.min_depth != null;
  var COMPONENTS = CFG.components || null;
  // One page can serve several products where only the rule and ranges differ — the pipe frame
  // kit is one page with a 1in and a 2in variant.
  var VARIANTS = CFG.variants || null;

  var quote = null, quoteKey = '', seq = 0;

  function money(n) {
    return n.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
  }
  function ftIn(n) { return Math.floor(n / 12) + "' " + (n % 12) + '"'; }

  function $sel(sel) { return document.querySelector(sel); }

  // Mirrors pricing.py's crossbar_distance_in() — CFG.crossbar_rule is only set true for the
  // commercial enclosure, whose own depth range (36-180in) is the only one that ever reaches the
  // 120in floor this starts at. The server's /quote response is still the authoritative value
  // (echoed back on spec.crossbar_in); this copy exists only so the drawing updates instantly as
  // depth changes, same as the local price estimate below.
  function crossbarIn(depthIn) {
    if (!isFinite(depthIn) || depthIn < 120) return null;
    if (depthIn <= 156) return 78; // 13' -> fixed 6'6"
    return depthIn / 2;
  }

  // ── spec ────────────────────────────────────────────────────────────────────
  function spec() {
    var finish = $sel(q('finish') + ':checked');
    var addons = [];
    document.querySelectorAll(q('addon') + ':checked').forEach(function (i) {
      addons.push(i.value);
    });
    // A "picked" add-on (Ceiling Track, Curtain Valance: one of several fixed sizes/prices, not
    // yes/no) is a <select> rather than a checkbox — pricing.py's validate() needs both which
    // add-on was turned on AND which size, so a blank/placeholder option means "not added" and
    // any other value both adds the key to `addons` and records the choice.
    var addonChoices = {};
    document.querySelectorAll(q('addon-pick')).forEach(function (sel) {
      if (sel.value) {
        addons.push(sel.dataset.key);
        addonChoices[sel.dataset.key] = sel.value;
      }
    });
    var out = {
      product_key: activeKey(),
      height_in: SINGLE ? 1 : parseInt(el.h.value, 10),
      width_in: parseInt(el.w.value, 10),
      border_finish: finish ? finish.value : '',
      addons: addons,
      addon_choices: addonChoices,
      // Route and Mulberry ride along as real variants on the draft. The server picks which,
      // from the item price and the final subtotal — the desk only says yes/no and which term.
      // Guarded because the PDP shell has no protection UI at all: both selectors then simply
      // find nothing, and this degrades to {route:false, mulberry_term:null}.
      protection: {
        route: !!(el.route && el.route.checked),
        mulberry_term: (function () {
          var m = $sel(q('mulberry') + ':checked');
          return m && m.value ? parseInt(m.value, 10) : null;
        })()
      }
    };
    if (HAS_DEPTH) out.depth_in = parseInt(el.d.value, 10);
    if (COMPONENTS) {
      var comps = [];
      document.querySelectorAll(q('component') + ':checked').forEach(function (i) {
        comps.push(i.value);
      });
      out.components = comps;
    }
    // A requirement of the 2in EMT pipe frame itself, not the surround (Bill, Sep 2026). On a
    // components page (the enclosure), only once "frame" is actually chosen. On a page with no
    // components at all — the pipe frame kit sold on its own — CFG.crossbar_rule (true only for
    // the 2in variant; applyVariant() merges it in) is already the whole answer.
    if (HAS_DEPTH && CFG.crossbar_rule &&
        (!COMPONENTS || (out.components || []).indexOf('frame') > -1)) {
      out.crossbar_in = crossbarIn(out.depth_in);
    }
    return out;
  }

  // Which product this page is currently building. Fixed for most pages; on a page with a
  // variant switch it follows the switch, because the rule and the ranges change with it.
  function activeKey() {
    var v = $sel(q('variant') + ':checked');
    return (v && v.value) || CFG.product_key;
  }

  // Pull the active variant's ranges onto CFG so validation, the sliders and the local estimate
  // all agree. Called before every render on a variant page.
  function applyVariant() {
    if (VARIANTS) {
      var v = VARIANTS[activeKey()];
      if (v) Object.keys(v).forEach(function (k) { CFG[k] = v[k]; });
    }
    [['h', 'min_height', 'max_height'], ['w', 'min_width', 'max_width'],
     ['d', 'min_depth', 'max_depth']].forEach(function (t) {
      var lo = CFG[t[1]], hi = CFG[t[2]];
      if (lo == null) return;
      [el[t[0]], el[t[0] + 'r']].forEach(function (i) {
        if (!i) return;
        i.min = lo; i.max = hi;
      });
      var i = el[t[0]];
      if (i) {
        var n = parseInt(i.value, 10);
        if (isFinite(n) && (n < lo || n > hi)) {
          i.value = Math.min(hi, Math.max(lo, n));
          if (el[t[0] + 'r']) el[t[0] + 'r'].value = i.value;
        }
      }
    });
  }

  function hasFinish() { return !!$sel(q('finish')); }

  function problems(s) {
    var bad = [];
    if (!SINGLE && (!isFinite(s.height_in) || s.height_in < CFG.min_height
                    || s.height_in > CFG.max_height))
      bad.push(LBL.height + ' must be ' + CFG.min_height + '–' + CFG.max_height + ' in.');
    if (!isFinite(s.width_in) || s.width_in < CFG.min_width || s.width_in > CFG.max_width)
      bad.push(LBL.width + ' must be ' + CFG.min_width + '–' + CFG.max_width + ' in.');
    if (HAS_DEPTH && (!isFinite(s.depth_in) || s.depth_in < CFG.min_depth
                      || s.depth_in > CFG.max_depth))
      bad.push('Depth must be ' + CFG.min_depth + '–' + CFG.max_depth + ' in.');
    if (hasFinish() && !s.border_finish) bad.push('Choose a border finish.');
    if (COMPONENTS && !(s.components || []).length)
      bad.push('Choose the surround, the pipe frame kit, or both.');
    return bad;
  }

  // ── drawing ─────────────────────────────────────────────────────────────────
  function grommets(start, end) {
    var span = end - start;
    if (span <= CORNER_IN * 2) return [start, end];
    var a = start + CORNER_IN, b = end - CORNER_IN;
    var gaps = Math.max(1, Math.round((b - a) / SPACING_IN));
    var step = (b - a) / gaps, pts = [start, a];
    for (var i = 1; i < gaps; i++) pts.push(a + i * step);
    pts.push(b, end);
    return pts;
  }

  function drawBar(s) {
    var w = s.width_in;
    // The viewBox scales with the product's own width, but the label's font-size below is a
    // fixed number of SVG units — fine for baffle/foam-pad (93"+, always plenty of viewBox to be
    // a small fraction of), but a small item like the valance (24-72") shrinks the viewBox enough
    // that the same fixed font-size reads as enormous. The floor only ever raises scaleW above w
    // for items already under it, so baffle and foam-pad compute byte-identical output to before.
    var scaleW = Math.max(w, 90);
    var barH = Math.max(scaleW * 0.06, 6), PADX = scaleW * 0.09;
    var vbW = scaleW + PADX * 2, vbH = barH + 46;
    var x = (vbW - w) / 2, y = 14, off = 14, tick = 3.5;
    el.stage.setAttribute('viewBox', '0 0 ' + vbW + ' ' + vbH);
    el.stage.innerHTML =
      '<rect x="' + x + '" y="' + y + '" width="' + w + '" height="' + barH +
        '" fill="var(--screen)" stroke="var(--dim)" stroke-width="0.8"/>' +
      '<line x1="' + x + '" y1="' + (y + barH + off) + '" x2="' + (x + w) + '" y2="' +
        (y + barH + off) + '" stroke="var(--dim)" stroke-width="0.7"/>' +
      '<line x1="' + x + '" y1="' + (y + barH + off - tick) + '" x2="' + x + '" y2="' +
        (y + barH + off + tick) + '" stroke="var(--dim)" stroke-width="0.7"/>' +
      '<line x1="' + (x + w) + '" y1="' + (y + barH + off - tick) + '" x2="' + (x + w) +
        '" y2="' + (y + barH + off + tick) + '" stroke="var(--dim)" stroke-width="0.7"/>' +
      '<text x="' + (x + w / 2) + '" y="' + (y + barH + off + 11) + '" font-size="8.5"' +
        ' text-anchor="middle" fill="var(--dim)">' + LBL.width.toUpperCase() + '  ' + w +
        '&quot; · ' + ftIn(w) + '</text>';
  }

  function drawBox(s) {
    var w = s.width_in, h = s.height_in, d = s.depth_in, dz = d * OBL;
    var pad = PAD, off = 13, tick = 3.5, q = 12;
    var x = pad, y = pad + dz;
    var bx = x + dz, by = y - dz;
    var vbW = w + dz + pad + q + 46;
    var vbH = h + dz + pad + 42;

    function ln(x1, y1, x2, y2, extra) {
      return '<line x1="' + x1.toFixed(1) + '" y1="' + y1.toFixed(1) + '" x2="' +
             x2.toFixed(1) + '" y2="' + y2.toFixed(1) +
             '" stroke="var(--dim)" stroke-width="0.7"' + (extra || '') + '/>';
    }
    function poly(pts, fill) {
      return '<polygon points="' + pts + '" fill="' + fill + '"/>';
    }

    var svg =
      poly([bx, by, bx + w, by, bx + w, by + h, bx, by + h].join(' '), 'none') +
      ln(bx, by, bx + w, by) + ln(bx + w, by, bx + w, by + h) +
      ln(bx, by + h, bx + w, by + h) + ln(bx, by, bx, by + h) +
      poly([x, y, x + w, y, bx + w, by, bx, by].join(' '), 'var(--shell-top)') +
      poly([x + w, y, x + w, y + h, bx + w, by + h, bx + w, by].join(' '), 'var(--shell-side)') +
      ln(x, y, bx, by) + ln(x + w, y, bx + w, by) +
      ln(x, y + h, bx, by + h) + ln(x + w, y + h, bx + w, by + h) +
      '<rect x="' + x + '" y="' + y + '" width="' + w + '" height="' + h +
        '" fill="none" stroke="var(--vinyl)" stroke-width="3"/>' +
      '<text x="' + (x + w / 2) + '" y="' + (y + h / 2) + '" font-size="9"' +
        ' text-anchor="middle" fill="var(--dim)">OPENING</text>' +
      ln(x, y + h + off, x + w, y + h + off) +
      ln(x, y + h + off - tick, x, y + h + off + tick) +
      ln(x + w, y + h + off - tick, x + w, y + h + off + tick) +
      '<text x="' + (x + w / 2) + '" y="' + (y + h + off + 10) + '" font-size="8.5"' +
        ' text-anchor="middle" fill="var(--dim)">' + w + '&quot; · ' + ftIn(w) + '</text>' +
      ln(x - off, y, x - off, y + h) +
      ln(x - off - tick, y, x - off + tick, y) +
      ln(x - off - tick, y + h, x - off + tick, y + h) +
      '<text x="' + (x - off - 4) + '" y="' + (y + h / 2) + '" font-size="8.5"' +
        ' transform="rotate(-90 ' + (x - off - 4) + ' ' + (y + h / 2) + ')"' +
        ' text-anchor="middle" fill="var(--dim)">' + h + '&quot; · ' + ftIn(h) + '</text>';

    // Crossbar: a line across the "top" face, offset along the depth axis by however far back
    // from the screen it sits (same x/y-per-inch-of-depth step as the back face itself, dz/d).
    if (s.crossbar_in) {
      var cbOff = s.crossbar_in * OBL;
      var cy1 = y - cbOff, cx1 = x + cbOff;
      svg +=
        ln(cx1, cy1, cx1 + w, cy1, ' stroke="var(--crossbar)" stroke-width="1.6"') +
        '<text x="' + (cx1 + w / 2) + '" y="' + (cy1 - 4) + '" font-size="8.5"' +
          ' text-anchor="middle" fill="var(--crossbar)">CROSSBAR — ' + s.crossbar_in +
          '&quot; · ' + ftIn(s.crossbar_in) + ' FROM SCREEN</text>';
    }

    var ax = x + w + q, ay = y + h + q;
    var bx2 = bx + w + q, by2 = by + h + q;
    var mx = (ax + bx2) / 2, my = (ay + by2) / 2;
    var t = tick * 0.7;
    svg +=
      ln(ax, ay, bx2, by2) +
      ln(ax - t, ay - t, ax + t, ay + t) +
      ln(bx2 - t, by2 - t, bx2 + t, by2 + t) +
      '<text x="' + (mx + 9).toFixed(1) + '" y="' + (my + 9).toFixed(1) + '" font-size="8.5"' +
        ' transform="rotate(-45 ' + (mx + 9).toFixed(1) + ' ' + (my + 9).toFixed(1) + ')"' +
        ' text-anchor="middle" fill="var(--dim)">DEPTH ' + d + '&quot; · ' + ftIn(d) + '</text>';

    el.stage.setAttribute('viewBox', (-pad * 0.7) + ' 0 ' + vbW + ' ' + vbH);
    el.stage.innerHTML = svg;
  }

  function draw(s) {
    if (HAS_DEPTH) return drawBox(s);
    if (SINGLE) return drawBar(s);
    var h = s.height_in, w = s.width_in;
    var pocket = /pocket|3 sides/i.test(s.border_finish);
    var x = PAD, y = PAD, vbW = w + PAD * 2.6, vbH = h + PAD * 2.6;

    var dots = [];
    grommets(x + VINYL_IN * 2, x + w - VINYL_IN * 2).forEach(function (gx) {
      dots.push([gx, y]);
      if (!pocket) dots.push([gx, y + h]);
    });
    grommets(y + VINYL_IN * 2, y + h - VINYL_IN * 2).forEach(function (gy) {
      dots.push([x, gy]);
      dots.push([x + w, gy]);
    });

    var off = 12, tick = 3.5;
    var svg =
      '<rect x="' + x + '" y="' + y + '" width="' + w + '" height="' + h + '" fill="var(--screen)"/>' +
      (HAS_GROMMETS
        ? '<rect x="' + x + '" y="' + y + '" width="' + w + '" height="' + h +
          '" fill="none" stroke="var(--vinyl)" stroke-width="' + VINYL_IN * 2 + '"/>'
        : '<rect x="' + x + '" y="' + y + '" width="' + w + '" height="' + h +
          '" fill="none" stroke="var(--dim)" stroke-width="0.8"/>') +
      (pocket
        ? '<text x="' + (x + w / 2) + '" y="' + (y + h - 6) + '" font-size="7" text-anchor="middle"' +
          ' fill="var(--dim)">2&quot; bottom pocket</text>'
        : '') +
      (HAS_GROMMETS ? dots : []).map(function (p) {
        return '<circle cx="' + p[0] + '" cy="' + p[1] + '" r="' + GROM_R + '" fill="var(--grommet)"/>' +
               '<circle cx="' + p[0] + '" cy="' + p[1] + '" r="' + GROM_HOLE + '" fill="#fff"/>';
      }).join('') +
      '<line x1="' + x + '" y1="' + (y + h + off) + '" x2="' + (x + w) + '" y2="' + (y + h + off) +
        '" stroke="var(--dim)" stroke-width="0.7"/>' +
      '<line x1="' + x + '" y1="' + (y + h + off - tick) + '" x2="' + x + '" y2="' +
        (y + h + off + tick) + '" stroke="var(--dim)" stroke-width="0.7"/>' +
      '<line x1="' + (x + w) + '" y1="' + (y + h + off - tick) + '" x2="' + (x + w) + '" y2="' +
        (y + h + off + tick) + '" stroke="var(--dim)" stroke-width="0.7"/>' +
      '<text x="' + (x + w / 2) + '" y="' + (y + h + off + 9) + '" font-size="8.5"' +
        ' text-anchor="middle" fill="var(--dim)">' + w + '&quot; · ' + ftIn(w) + '</text>' +
      '<line x1="' + (x + w + off) + '" y1="' + y + '" x2="' + (x + w + off) + '" y2="' + (y + h) +
        '" stroke="var(--dim)" stroke-width="0.7"/>' +
      '<line x1="' + (x + w + off - tick) + '" y1="' + y + '" x2="' + (x + w + off + tick) +
        '" y2="' + y + '" stroke="var(--dim)" stroke-width="0.7"/>' +
      '<line x1="' + (x + w + off - tick) + '" y1="' + (y + h) + '" x2="' + (x + w + off + tick) +
        '" y2="' + (y + h) + '" stroke="var(--dim)" stroke-width="0.7"/>' +
      '<text x="' + (x + w + off + 6) + '" y="' + (y + h / 2) + '" font-size="8.5"' +
        ' transform="rotate(90 ' + (x + w + off + 6) + ' ' + (y + h / 2) + ')"' +
        ' text-anchor="middle" fill="var(--dim)">' + h + '&quot; · ' + ftIn(h) + '</text>';

    el.stage.setAttribute('viewBox', '0 0 ' + vbW + ' ' + vbH);
    el.stage.innerHTML = svg;
  }

  // ── pricing (local estimate only — /quote is authoritative, /checkout recomputes again) ──────
  function to99(raw) {
    var t = Math.floor(raw) + 0.99;
    return t < raw ? t + 1 : t;
  }

  function ladderPrice(p, s) {
    var sqft = s.width_in * s.height_in / 144;
    var depthFt = s.depth_in / 12;
    return p.base + p.per_sqft * sqft + p.per_depth_ft * (depthFt - 5);
  }

  // Most derived add-ons (the blackout screen matching either impact screen) are a simple
  // same-axis offset: `offset_height`/`offset_width` shift the host's own height/width. An
  // enclosure's landing pad sizes off a rule that isn't that shape at all — see pricing.py's
  // DIY_LANDING_PAD_RULE / COMMERCIAL_LANDING_PAD_RULE, copied into `d` verbatim by
  // sync_client_config.py's wanted_derived() — so `length_source` names which rule to run
  // instead. Both are read as plain config here; neither is hardcoded business logic, so a third
  // add-on with its own rule only needs a new branch here and in pricing.py, not a rewrite.
  function derivedSize(d, s) {
    if (d.length_source === 'width_step') {
      var w1 = s.width_in + (d.width_offset || 0);
      return { height: w1 < d.step_threshold_in ? d.step_low_in : d.step_high_in, width: w1 };
    }
    if (d.length_source === 'depth_tier') {
      if (!isFinite(s.depth_in)) return null;
      var w2 = s.width_in + (d.width_offset || 0);
      var best = null;
      (d.tiers_in || []).forEach(function (t) {
        if (best === null || Math.abs(t - s.depth_in) < Math.abs(best - s.depth_in)) best = t;
      });
      if (best === null) return null;
      return { height: best, width: w2 };
    }
    return { height: s.height_in + (d.offset_height || 0), width: s.width_in + (d.offset_width || 0) };
  }

  function derivedAddon(key, s) {
    var d = (CFG.derived || {})[key];
    if (!d || !isFinite(s.height_in) || !isFinite(s.width_in)) return null;
    var sized = derivedSize(d, s);
    if (!sized) return null;
    var h = sized.height, w = sized.width;
    if (h < d.min_height || h > d.max_height || w < d.min_width || w > d.max_width) return null;
    // Turf's model charges its intercept per inch of WIDTH rather than once flat (see
    // pricing.py's screen_price(), the setup_per_width_in branch) — a shape the blackout screen's
    // rate_len/rate_wid or flat-area formulas never needed.
    var raw = d.setup_per_width != null ? d.setup_per_width * w + d.rate * h * w
      : d.rate_len ? d.setup + d.rate_len * h + d.rate_wid * w
      : d.setup + d.rate * h * w;
    return { height: h, width: w, price: to99(raw) };
  }

  function localEstimate(s) {
    var total = 0, uplift = CFG.uplift || 1;
    if (COMPONENTS) {
      (s.components || []).forEach(function (k) {
        var c = COMPONENTS[k];
        if (c) total += to99(uplift * ladderPrice(c, s));
      });
    } else if (HAS_DEPTH && CFG.ladder) {
      total = to99(uplift * ladderPrice(CFG.ladder, s));
    } else if (CFG.setup_per_width) {
      total = to99(CFG.setup_per_width * s.width_in + CFG.rate * s.height_in * s.width_in);
    } else if (CFG.rate_len) {
      total = to99(CFG.setup + s.height_in * CFG.rate_len + s.width_in * CFG.rate_wid);
    } else {
      total = to99(s.height_in * s.width_in * CFG.rate + CFG.setup);
    }
    s.addons.forEach(function (k) {
      var d = derivedAddon(k, s);
      total += d ? d.price : (CFG.addons[k] || 0);
    });
    return total;
  }

  // ── rendering helpers ──────────────────────────────────────────────────────────
  function renderRows(d) {
    var s = spec();
    var out = [
      SINGLE
        ? ['Finished ' + LBL.width.toLowerCase(), s.width_in + '"', false]
        : ['Finished size', s.width_in + '" W × ' + s.height_in + '" H'
                            + (HAS_DEPTH ? ' × ' + s.depth_in + '" D' : ''), false]
    ];
    if (s.border_finish) out.push(['Border finish', s.border_finish, false]);
    if (s.crossbar_in) out.push(['Crossbar', s.crossbar_in + '" (' + ftIn(s.crossbar_in) +
                                 ') from screen', false]);
    d.lines.forEach(function (l) {
      out.push([lineLabel(l.title), money(parseFloat(l.unit_price)), true]);
    });
    out.push(['Lead time', d.lead_time, false]);
    el.rows.innerHTML = out.map(function (r) {
      return '<div class="r"><span>' + r[0] + '</span><span' + (r[2] ? ' class="amt"' : '') +
             '>' + r[1] + '</span></div>';
    }).join('') +
      '<div class="r total"><span>Total (before tax)</span><span class="amt">' +
      money(parseFloat(d.total)) + '</span></div>';
  }

  function showStock(stock, onUseStock) {
    if (!el.stock) return;
    if (!stock) { el.stock.classList.add('hidden'); el.stock.innerHTML = ''; return; }
    var saving = parseFloat(stock.saving || '0');
    var savingTxt = saving > 0 ? ' Saves ' + money(saving) + ' versus a custom cut.' : '';
    var lead = stock.lead_time ? stock.lead_time.toLowerCase() : '';
    var vs = (stock.lead_time && stock.custom_lead_time && stock.lead_time !== stock.custom_lead_time)
      ? ' instead of ' + stock.custom_lead_time.toLowerCase().replace(/^ships in /, '') : '';
    var dims = stock.height_in + '" H × ' + stock.width_in + '" W'
      + (stock.depth_in ? ' × ' + stock.depth_in + '" D' : '');

    if (stock.kind === 'exact') {
      el.stock.innerHTML = '<b>This is a stock size.</b>' + stock.label + ' is '
        + money(parseFloat(stock.price)) + (lead ? ', ' + lead : '') + vs + '.' + savingTxt
        + ' Sell the shelf item rather than cutting a duplicate.';
    } else {
      el.stock.innerHTML = '<b>A stock size is close to this one.</b>' + stock.label
        + ' (' + dims + ') is ' + money(parseFloat(stock.price))
        + (lead ? ', ' + lead : '') + vs + '.' + savingTxt
        + '<button type="button" ' + ATTR + '="useStock">Use the stock size</button>';
      var btn = el.stock.querySelector(q('useStock'));
      btn.addEventListener('click', function () {
        el.h.value = el.hr.value = stock.height_in;
        el.w.value = el.wr.value = stock.width_in;
        if (el.d && stock.depth_in) el.d.value = el.dr.value = stock.depth_in;
        if (onUseStock) onUseStock();
      });
    }
    el.stock.classList.remove('hidden');
  }

  function lineLabel(title) {
    var parts = String(title || '').split(' — ').map(function (t) { return t.trim(); });
    if (parts.length > 1 && /\d\s*"/.test(parts[parts.length - 1])) parts.pop();
    var label = parts.length > 1 ? parts.slice(1).join(' — ') : parts[0];
    label = label.charAt(0).toUpperCase() + label.slice(1);
    if (label.length > 64) label = label.slice(0, 62).replace(/[\s\-–—,:;]+$/, '') + '…';
    return label;
  }

  function renderDerivedAddons(s) {
    var changed = false;
    Object.keys(CFG.derived || {}).forEach(function (k) {
      var row = document.querySelector('[data-addon-row="' + k + '"]');
      var out = document.querySelector('[data-addon-price="' + k + '"]');
      var box = row && row.querySelector('input' + q('addon'));
      var d = derivedAddon(k, s);
      if (out) out.textContent = d ? money(d.price) : 'not available at this size';
      if (box) {
        box.disabled = !d;
        if (!d && box.checked) { box.checked = false; changed = true; }
      }
      if (row) row.classList.toggle('disabled', !d);
    });
    return changed;
  }

  // ── quote fetching ──────────────────────────────────────────────────────────────
  function keyOf(s) { return JSON.stringify(s); }

  function fetchQuote(s) {
    var mine = ++seq;
    return apiFn('/quote', { method: 'POST', body: JSON.stringify(s) })
      .then(readJson).then(function (res) {
        if (!res.ok) throw new Error(res.data.error || 'Could not price that.');
        if (mine === seq) {
          quote = res.data;
          quoteKey = keyOf(s);
          el.price.textContent = money(parseFloat(quote.total));
          el.priceSub.textContent = quote.lead_time || '';
          renderRows(quote);
        }
        return res.data;
      });
  }

  function refreshQuote() {
    return fetchQuote(spec()).catch(function (ex) {
      if (el.specErr) el.specErr.textContent = ex.message;
      throw ex;
    });
  }

  // Whatever is in flight, end up holding a quote for the spec being saved.
  function settledQuote(s) {
    if (quote && quoteKey === keyOf(s)) return Promise.resolve(quote);
    return fetchQuote(s);
  }

  // ── customer form (shared shape: agent desk and PDP checkout dialog both collect this) ────────
  var FIELDS = ['first_name', 'last_name', 'email', 'phone',
                'address1', 'address2', 'city', 'province', 'zip', 'country'];
  var OPTIONAL = { address2: true };

  function collect(fieldEl) {
    var out = {}, missing = [];
    FIELDS.forEach(function (k) {
      var i = fieldEl(k);
      var v = i ? String(i.value || '').trim() : '';
      out[k] = v;
      if (!v && !OPTIONAL[k]) missing.push(k);
    });
    return { customer: out, missing: missing };
  }

  function emailValid(v) { return /^[^@\s]+@[^@\s.]+(\.[^@\s.]+)+$/.test(v || ''); }

  return {
    // config-derived flags, read-only for the shell
    SINGLE: SINGLE, HAS_DEPTH: HAS_DEPTH, HAS_GROMMETS: HAS_GROMMETS,
    COMPONENTS: COMPONENTS, VARIANTS: VARIANTS, LBL: LBL,
    FIELDS: FIELDS,
    // pure helpers
    money: money, ftIn: ftIn, emailValid: emailValid,
    // spec / validation
    spec: spec, activeKey: activeKey, applyVariant: applyVariant, hasFinish: hasFinish,
    problems: problems,
    // drawing
    draw: draw,
    // pricing
    localEstimate: localEstimate, derivedAddon: derivedAddon, to99: to99,
    // rendering
    renderRows: renderRows, showStock: showStock, lineLabel: lineLabel,
    renderDerivedAddons: renderDerivedAddons,
    // quoting
    keyOf: keyOf, fetchQuote: fetchQuote, refreshQuote: refreshQuote, settledQuote: settledQuote,
    getQuote: function () { return quote; },
    clearQuote: function () { quote = null; quoteKey = ''; },
    // customer form
    collect: collect,
  };
};